import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { db } from '../firebase';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#F4F6F9',
    fontFamily: 'system-ui, sans-serif',
  },
  main: {
    marginLeft: 220,
    padding: 24,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#888',
    marginBottom: 24,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: 12,
    border: '0.5px solid #e0e0e0',
    padding: 24,
    maxWidth: 600,
  },
  codeBanner: {
    backgroundColor: '#F4F6F9',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  codeBannerLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  codeBannerValue: {
    fontFamily: 'monospace',
    fontSize: 18,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 2,
  },
  codeBannerHint: {
    fontSize: 12,
    color: '#888',
    marginLeft: 'auto',
    fontStyle: 'italic',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 16,
    marginTop: 24,
    paddingBottom: 8,
    borderBottom: '1px solid #eee',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: '600',
    color: '#555',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: 'white',
  },
  planToggle: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  planBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    border: '1.5px solid #ddd',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#888',
  },
  planBtnActive: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    border: '1.5px solid #1D6FA4',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: '#E6F1F9',
    color: '#1D6FA4',
  },
  planBtnActiveOrange: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    border: '1.5px solid #E8620A',
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: '#FDF0E8',
    color: '#E8620A',
  },
  submitBtn: {
    backgroundColor: '#E8620A',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 8,
  },
  cancelBtn: {
    backgroundColor: 'white',
    color: '#888',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 8,
    marginLeft: 12,
  },
  success: {
    backgroundColor: '#EAF3DE',
    color: '#3B6D11',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 14,
    marginTop: 12,
    fontWeight: '600',
    display: 'inline-block',
    marginLeft: 12,
  },
  error: {
    backgroundColor: '#FEE2E2',
    color: '#CC0000',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 16,
  },
  aepSection: {
    backgroundColor: '#F4F6F9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  aepLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

function formatPremium(value) {
  if (!value) return '';
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return '';
  return '$' + num.toFixed(2);
}

export default function EditClient() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [planType, setPlanType] = useState('Supplement');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [lookupCode, setLookupCode] = useState('');

  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    state: 'WI',
    carrier: '',
    plan: '',
    premium: '',
    startDate: '',
    reviewInterval: '1',
    birthdayMonth: '',
    birthdayDay: '',
    notes: '',
    aepStatus: '',
    partDStatus: '',
  });

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const ref = doc(db, 'clients', clientId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setPlanType(data.planType || 'Supplement');
          setLookupCode(data.lookupCode || '');
          setForm({
            name: data.name || '',
            mobile: data.mobile || '',
            email: data.email || '',
            state: data.state || 'WI',
            carrier: data.carrier || '',
            plan: data.plan || '',
            premium: data.premium || '',
            startDate: data.startDate || '',
            reviewInterval: data.reviewInterval || '1',
            birthdayMonth: data.birthdayMonth || '',
            birthdayDay: data.birthdayDay || '',
            notes: data.notes || '',
            aepStatus: data.aepStatus || '',
            partDStatus: data.partDStatus || '',
          });
        }
      } catch (err) {
        setError('Error loading client data.');
      }
      setFetching(false);
    };
    fetchClient();
  }, [clientId]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Email format is invalid.');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'clients', clientId), {
        name: form.name,
        mobile: form.mobile,
        email: form.email.trim(),
        state: form.state,
        planType: planType,
        carrier: form.carrier,
        plan: form.plan,
        premium: formatPremium(form.premium),
        startDate: form.startDate,
        reviewInterval: form.reviewInterval,
        birthdayMonth: form.birthdayMonth,
        birthdayDay: form.birthdayDay,
        notes: form.notes,
        aepStatus: planType === 'Advantage' ? (form.aepStatus || null) : null,
        partDStatus: planType === 'Supplement' ? (form.partDStatus || null) : null,
        updatedAt: new Date(),
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        navigate('/clients');
      }, 2000);
    } catch (err) {
      setError('Error saving changes. Please try again.');
    }
    setLoading(false);
  };

  if (fetching) return <div style={{ padding: 40 }}>Loading...</div>;

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Edit Client</div>
        <div style={styles.pageSub}>Changes will update the client's app immediately.</div>

        <div style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          {lookupCode && (
            <div style={styles.codeBanner}>
              <span style={styles.codeBannerLabel}>Access Code</span>
              <span style={styles.codeBannerValue}>{lookupCode}</span>
              <span style={styles.codeBannerHint}>Read this to your client to open the app</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.sectionTitle}>Client Information</div>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Full Name</label>
                <input style={styles.input} value={form.name}
                  onChange={e => handleChange('name', e.target.value)}
                  required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Mobile Number</label>
                <input style={styles.input} value={form.mobile}
                  onChange={e => handleChange('mobile', e.target.value)}
                  required />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email (Optional)</label>
              <input style={styles.input} value={form.email}
                onChange={e => handleChange('email', e.target.value)} />
            </div>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>State</label>
                <select style={styles.select} value={form.state}
                  onChange={e => handleChange('state', e.target.value)}>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Birthday (Optional)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ ...styles.select, flex: 1 }} value={form.birthdayMonth}
                    onChange={e => handleChange('birthdayMonth', e.target.value)}>
                    <option value="">Month</option>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select style={{ ...styles.select, width: 80 }} value={form.birthdayDay}
                    onChange={e => handleChange('birthdayDay', e.target.value)}>
                    <option value="">Day</option>
                    {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.sectionTitle}>Plan Information</div>
            <div style={styles.field}>
              <label style={styles.label}>Plan Type</label>
              <div style={styles.planToggle}>
                <button type="button"
                  style={planType === 'Supplement' ? styles.planBtnActive : styles.planBtn}
                  onClick={() => setPlanType('Supplement')}>
                  Medicare Supplement
                </button>
                <button type="button"
                  style={planType === 'Advantage' ? styles.planBtnActiveOrange : styles.planBtn}
                  onClick={() => setPlanType('Advantage')}>
                  Medicare Advantage
                </button>
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Carrier</label>
                <input style={styles.input} value={form.carrier}
                  onChange={e => handleChange('carrier', e.target.value)}
                  required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Plan</label>
                <input style={styles.input} value={form.plan}
                  onChange={e => handleChange('plan', e.target.value)}
                  required />
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Monthly Premium</label>
                <input style={styles.input} value={form.premium}
                  onChange={e => handleChange('premium', e.target.value)}
                  onBlur={e => handleChange('premium', formatPremium(e.target.value))}
                  required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Plan Start Date</label>
                <input style={styles.input} type="date" value={form.startDate}
                  onChange={e => handleChange('startDate', e.target.value)}
                  required />
              </div>
            </div>

            {planType === 'Supplement' && (
              <div style={styles.field}>
                <label style={styles.label}>Review Interval</label>
                <select style={styles.select} value={form.reviewInterval}
                  onChange={e => handleChange('reviewInterval', e.target.value)}>
                  <option value="1">Every year</option>
                  <option value="2">Every 2 years</option>
                  <option value="3">Every 3 years</option>
                </select>
              </div>
            )}

            {planType === 'Advantage' && (
              <>
                <div style={styles.sectionTitle}>AEP Status</div>
                <div style={styles.aepSection}>
                  <div style={styles.aepLabel}>Current AEP Status</div>
                  <select style={styles.select} value={form.aepStatus}
                    onChange={e => handleChange('aepStatus', e.target.value)}>
                    <option value="">No response</option>
                    <option value="staying">Keeping plan</option>
                    <option value="scheduled">Appointment scheduled</option>
                    <option value="spoke">Spoke with client</option>
                  </select>
                </div>
              </>
            )}

            <div style={styles.sectionTitle}>Notes</div>
            <div style={styles.field}>
              <label style={styles.label}>Agent Notes (not visible to client)</label>
              <textarea style={{ ...styles.input, height: 80, resize: 'vertical' }}
                value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                placeholder="Spoke 10/3, staying with Humana..." />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={styles.submitBtn} type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button style={styles.cancelBtn} type="button"
                onClick={() => navigate('/clients')}>
                Cancel
              </button>
              {success && <div style={styles.success}>Changes saved.</div>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}